import { Environment, Lightformer } from '@react-three/drei'
import { useMemo } from 'react'
import type { PerfilVeiculo } from '../../dominio/didatico/perfilVeiculo'

/**
 * Iluminação de estúdio por mapa de ambiente, gerada em tempo de execução.
 *
 * Metal sem mapa de ambiente reflete o nada, e o nada é preto: era por isso que
 * a carroceria lia como papelão mesmo com `metalness` alto. Aqui o `Environment`
 * recebe `Lightformer` como filhos e monta o PMREM localmente — sem `preset` e
 * sem `files`, nada é baixado. A aula acontece em sala, às vezes sem rede, então
 * depender de HDRI hospedado não é opção.
 *
 * O rig é o clássico de fotografia de automóvel: uma banca larga em cima e duas
 * faixas compridas rasantes nos flancos. São as faixas que desenham o realce
 * corrido pela lateral — o reflexo que faz o olho ler "chapa de carro" em vez de
 * "caixa cinza".
 *
 * As luzes são memorizadas de propósito. O `Environment` do drei reassa o mapa
 * num `useLayoutEffect` que depende da identidade de `children`: recriar os
 * lightformers a cada render faz o efeito disparar, invalidar o quadro e
 * renderizar de novo, sem fim. Nas cenas paradas isso se acomodava; no Table top
 * e na extração, que redesenham a todo quadro, estourava a profundidade de
 * atualização do React. O rig só depende do tamanho do veículo, e é só isso que
 * entra na lista de dependências.
 */
export function AmbienteEstudio({ perfil }: { perfil: PerfilVeiculo }) {
  // O mapa é assado uma vez a partir da origem, então as medidas acompanham o
  // veículo em vez de serem fixas: o sedan é mais longo que o hatch e as faixas
  // precisam cobrir o flanco inteiro nos dois.
  const comprimento = perfil.comprimento
  const alturaFlanco = perfil.tetoY * 0.7

  const luzes = useMemo(
    () => (
      <>
        {/* Banca superior: a luz principal, larga o bastante para cobrir capô,
            teto e tampa de uma vez só. */}
        <Lightformer
          form="rect"
          intensity={3.2}
          color="#fff6ec"
          position={[0, 6, 0.5]}
          rotation={[-Math.PI / 2, 0, 0]}
          scale={[comprimento * 1.1, comprimento * 0.55]}
        />

        {/* Faixas rasantes nos dois flancos: são elas que dão o realce corrido. */}
        {[1, -1].map((sinal) => (
          <Lightformer
            key={sinal}
            form="rect"
            intensity={2.1}
            color="#eaf2ff"
            position={[sinal * 5, alturaFlanco, 0]}
            rotation={[0, (sinal * Math.PI) / 2, 0]}
            scale={[comprimento * 1.3, alturaFlanco * 1.6]}
          />
        ))}

        {/* Contorno traseiro frio: separa a silhueta do fundo escuro da cena. */}
        <Lightformer
          form="rect"
          intensity={1.4}
          color="#b8d4ff"
          position={[0, alturaFlanco * 1.4, -5]}
          rotation={[0, Math.PI, 0]}
          scale={[comprimento * 0.8, alturaFlanco * 2]}
        />

        {/* Anel de estúdio, no estilo do exemplo *building-live-envmaps*: num
            cilindro hidráulico, numa haste cromada ou num cubo de roda, um
            realce redondo lê "metal torneado" onde as faixas retas só desenham
            uma linha. Fica alto e à frente do lado do motorista, de onde a
            câmera padrão olha, e fraco o bastante para não competir com a
            banca superior na lataria. */}
        <Lightformer
          form="ring"
          intensity={2.4}
          color="#fff3e6"
          position={[comprimento * 0.55, alturaFlanco * 2.6, comprimento * 0.7]}
          scale={comprimento * 0.32}
        />

        {/* Rebatedor de piso fraco: evita que a parte de baixo da carroceria e as
            peças de mecânica caiam para preto puro quando o capô está aberto. */}
        <Lightformer
          form="rect"
          intensity={0.55}
          color="#6f7c8a"
          position={[0, -2.5, 0]}
          rotation={[Math.PI / 2, 0, 0]}
          scale={[comprimento, comprimento * 0.6]}
        />
      </>
    ),
    [comprimento, alturaFlanco],
  )

  return (
    <Environment frames={1} resolution={256} environmentIntensity={1}>
      {luzes}
    </Environment>
  )
}
